import React from "react";
import { observer } from "mobx-react";
import { DesignShowcaseCreateStore } from "../../store";
import { DesignShowcaseCreateForm } from "./DesignShowcaseCreateForm";
import { ErrorDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { ShowcaseStatus } from "../../../../api/graphql";

export type DesignShowcaseCreateProps = {
  designShowcaseCreateStore: DesignShowcaseCreateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const DesignShowcaseCreate: React.FC<DesignShowcaseCreateProps> =
  observer(
    ({ designShowcaseCreateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={designShowcaseCreateStore.isSaving} />
          <DesignShowcaseCreateForm
            designShowcaseCreateStore={designShowcaseCreateStore}
            codeError={designShowcaseCreateStore.errors.code?.message}
            onCodeChange={(value: string) => {
              designShowcaseCreateStore.setCodeValue(value);
            }}
            code={designShowcaseCreateStore.entityValues.code}
            nameError={designShowcaseCreateStore.errors.name?.message}
            onNameChange={(value: string) => {
              designShowcaseCreateStore.setNameValue(value);
            }}
            name={designShowcaseCreateStore.entityValues.name}
            summaryError={designShowcaseCreateStore.errors.summary?.message}
            onSummaryChange={(value: string) => {
              designShowcaseCreateStore.setSummaryValue(value);
            }}
            summary={designShowcaseCreateStore.entityValues.summary}
            statusError={designShowcaseCreateStore.errors.status?.message}
            statusOptions={designShowcaseCreateStore.getStatusOptions()}
            onStatusChange={(value: ShowcaseStatus) => {
              designShowcaseCreateStore.setStatusValue(value);
            }}
            status={designShowcaseCreateStore.entityValues.status}
            categoryError={designShowcaseCreateStore.errors.category?.message}
            onCategoryChange={(value) => {
              designShowcaseCreateStore.setCategoryValue(value);
            }}
            categorySelectedValue={
              designShowcaseCreateStore.entityValues.showCategory
            }
            onCategorySelected={(value) => {
              designShowcaseCreateStore.setSelectedCategory(value);
            }}
            categoryOptions={designShowcaseCreateStore.getCategoryOptions()}
            relatedCategoriesError={
              designShowcaseCreateStore.errors.relatedCategories?.message
            }
            onRelatedCategoriesChange={(value) => {
              designShowcaseCreateStore.setRelatedCategoriesValue(value);
            }}
            relatedCategoriesSelectedValues={
              designShowcaseCreateStore.entityValues.showRelatedCategories
            }
            onRelatedCategoriesSelected={(value) => {
              designShowcaseCreateStore.setSelectedRelatedCategories(value);
            }}
            relatedCategoriesOptions={designShowcaseCreateStore.getRelatedCategoriesOptions()}
            appliedStatusesError={
              designShowcaseCreateStore.errors.appliedStatuses?.message
            }
            appliedStatusesOptions={designShowcaseCreateStore.getAppliedStatusesOptions()}
            onAppliedStatusesChange={(value: ShowcaseStatus[]) => {
              designShowcaseCreateStore.setAppliedStatusesValue(value);
            }}
            appliedStatuses={
              designShowcaseCreateStore.entityValues.appliedStatuses
            }
            quantityError={designShowcaseCreateStore.errors.quantity?.message}
            onQuantityChange={(value: number) => {
              designShowcaseCreateStore.setQuantityValue(value);
            }}
            quantity={designShowcaseCreateStore.entityValues.quantity}
            durationInYearsError={
              designShowcaseCreateStore.errors.durationInYears?.message
            }
            onDurationInYearsChange={(value: number | undefined) => {
              designShowcaseCreateStore.setDurationInYearsValue(value);
            }}
            durationInYears={
              designShowcaseCreateStore.entityValues.durationInYears
            }
            amountError={designShowcaseCreateStore.errors.amount?.message}
            onAmountChange={(value: number) => {
              designShowcaseCreateStore.setAmountValue(value);
            }}
            amount={designShowcaseCreateStore.entityValues.amount}
            ratioError={designShowcaseCreateStore.errors.ratio?.message}
            onRatioChange={(value: number | undefined) => {
              designShowcaseCreateStore.setRatioValue(value);
            }}
            ratio={designShowcaseCreateStore.entityValues.ratio}
            startDateError={designShowcaseCreateStore.errors.startDate?.message}
            onStartDateChange={(value) => {
              designShowcaseCreateStore.setStartDateValue(value);
            }}
            startDate={designShowcaseCreateStore.entityValues.startDate}
            expiresAtError={designShowcaseCreateStore.errors.expiresAt?.message}
            onExpiresAtChange={(value) => {
              designShowcaseCreateStore.setExpiresAtValue(value);
            }}
            expiresAt={designShowcaseCreateStore.entityValues.expiresAt}
            registeredAtError={
              designShowcaseCreateStore.errors.registeredAt?.message
            }
            onRegisteredAtChange={(value) => {
              designShowcaseCreateStore.setRegisteredAtValue(value);
            }}
            registeredAt={designShowcaseCreateStore.entityValues.registeredAt}
            activeError={designShowcaseCreateStore.errors.active?.message}
            onActiveChange={(value: boolean) => {
              designShowcaseCreateStore.setActiveValue(value);
            }}
            active={designShowcaseCreateStore.entityValues.active}
            highlightedError={
              designShowcaseCreateStore.errors.highlighted?.message
            }
            onHighlightedChange={(value: boolean) => {
              designShowcaseCreateStore.setHighlightedValue(value);
            }}
            highlighted={designShowcaseCreateStore.entityValues.highlighted}
            phoneError={designShowcaseCreateStore.errors.phone?.message}
            onPhoneChange={(value: string) => {
              designShowcaseCreateStore.setPhoneValue(value);
            }}
            phone={designShowcaseCreateStore.entityValues.phone}
            taxIdError={designShowcaseCreateStore.errors.taxId?.message}
            onTaxIdChange={(value: string) => {
              designShowcaseCreateStore.setTaxIdValue(value);
            }}
            taxId={designShowcaseCreateStore.entityValues.taxId}
            attachmentOnRestoreFile={(id: string) =>
              designShowcaseCreateStore.attachmentRestoreFile(id)
            }
            attachmentOnLoadFile={(file: any) =>
              designShowcaseCreateStore.attachmentUploadFile(file)
            }
            attachmentOnCommitFile={(changes) =>
              designShowcaseCreateStore.attachmentCommitFile(changes)
            }
            attachmentFiles={
              designShowcaseCreateStore.entityValues.__attachment_files
            }
            attachmentError={
              designShowcaseCreateStore.errors.attachment?.message
            }
            anyFileOnRestoreFile={(id: string) =>
              designShowcaseCreateStore.anyFileRestoreFile(id)
            }
            anyFileOnLoadFile={(file: any) =>
              designShowcaseCreateStore.anyFileUploadFile(file)
            }
            anyFileOnCommitFile={(changes) =>
              designShowcaseCreateStore.anyFileCommitFile(changes)
            }
            anyFileFiles={
              designShowcaseCreateStore.entityValues.__anyFile_files
            }
            anyFileError={designShowcaseCreateStore.errors.anyFile?.message}
            onAvatarChange={(blob: Blob) =>
              designShowcaseCreateStore.avatarUploadBlob(blob)
            }
            onAvatarDelete={() => designShowcaseCreateStore.avatarDelete()}
            keywordsError={designShowcaseCreateStore.errors.keywords?.message}
            onKeywordsChange={(value: string[]) => {
              designShowcaseCreateStore.setKeywordsValue(value);
            }}
            keywords={designShowcaseCreateStore.entityValues.keywords}
            bodyError={designShowcaseCreateStore.errors.body?.message}
            onBodyChange={(value: string) => {
              designShowcaseCreateStore.setBodyValue(value);
            }}
            body={designShowcaseCreateStore.entityValues.body}
            metadataError={designShowcaseCreateStore.errors.metadata?.message}
            onMetadataChange={(value: string) => {
              designShowcaseCreateStore.setMetadataValue(value);
            }}
            metadata={designShowcaseCreateStore.entityValues.metadata}
            internalNoteError={
              designShowcaseCreateStore.errors.internalNote?.message
            }
            onInternalNoteChange={(value: string) => {
              designShowcaseCreateStore.setInternalNoteValue(value);
            }}
            internalNote={designShowcaseCreateStore.entityValues.internalNote}
            apiKeyError={designShowcaseCreateStore.errors.apiKey?.message}
            onApiKeyChange={(value: string) => {
              designShowcaseCreateStore.setApiKeyValue(value);
            }}
            apiKey={designShowcaseCreateStore.entityValues.apiKey}
            showcaseLineCreateStore={designShowcaseCreateStore.linesCreateStore}
            linesVisibleColumns={designShowcaseCreateStore.linesVisibleColumns}
            linesOnRestore={(id: string) =>
              designShowcaseCreateStore.linesRestore(id)
            }
            linesOnCommitChanges={(changes) =>
              designShowcaseCreateStore.linesCommitChanges(changes)
            }
            linesValidateChanges={(changes) =>
              designShowcaseCreateStore.linesValidateChanges(changes)
            }
            linesRows={designShowcaseCreateStore.entityValues.showLines}
            linesError={designShowcaseCreateStore.errors.lines?.message}
            dummyCRUDRestoredObservable={
              designShowcaseCreateStore.dummyCRUDRestoredObservable
            }
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={designShowcaseCreateStore.isProcessing}
            onSave={() => designShowcaseCreateStore.saveNewDesignShowcase()}
            onCancel={() => designShowcaseCreateStore.cancelNewDesignShowcase()}
            viewFormCategory={() =>
              designShowcaseCreateStore.viewFormCategory()
            }
          />
          <ErrorDialog
            open={designShowcaseCreateStore.errorDialogOpened}
            title={designShowcaseCreateStore.errorTitle}
            description={designShowcaseCreateStore.errorDescription}
            onClose={() => designShowcaseCreateStore.closeErrorDialog()}
          />
        </>
      );
    }
  );
